import { TFunction } from "i18next";

import {
  claimsLoadDataFunc,
  customRecordsLoadDataFunc,
  customSignalEventDefinitionsLoadDataFunc,
  dealersLoadDataFunc,
  failureModeEventLoadDataFunc,
  failureModeLoadDataFunc,
  failureModeRiskModelPredictionLoadDataFunc,
  failureModesLoadDataFunc,
  groupsLoadDataFunc,
  inspectionsLoadDataFunc,
  investigationsLoadDataFunc,
  issuesLoadDataFunc,
  loadSensorReadingsValuesFunc,
  loadSensorValuesFunc,
  loadTenantsFunc,
  manufactureLineExitsLoadDataFunc,
  partsLoadDataFunc,
  repairsLoadDataFunc,
  riskModelPredictionLoadDataFunc,
  serviceCentersLoadDataFunc,
  serviceRecordsLoadDataFunc,
  signalEventsOccurrencesLoadDataFunc,
  signalEventsValuesLoadDataFunc,
  smartGroupsLoadDataFunc,
  smartGroupsV2LoadDataFunc,
  transformInitialSelectedFailureMode,
  transformInitialSelectedSensors,
  transformInitialSelectedSmartGroup,
  transformInitialSelectedSmartGroupV2,
  transformInitialSelectedTransportCategory,
  transformInitialSelectedVectorCluster,
  transportCategoriesLoadDataFunc,
  vectorClustersLoadDataFunc,
  vehicleECUsLoadDataFunc,
  vehicleLoadDataFunc,
  vehicleOptionsLoadDataFunc,
  vehiclePartsLoadDataFunc,
  vehicleRiskModelPredictionLoadDataFunc,
  vehicleTagsLoadDataFunc,
  workOrderLoadDataFunc,
  workOrderPartLoadDataFunc,
} from "shared/api/filter.utils";
import { getSmartGroup, getSmartGroupV2 } from "shared/api/smartGroups/api";

import {
  FilterOperator,
  FilterSchemaItem,
  FilterType,
} from "features/ui/Filters/types";

import { APIListValuesRequest } from "./api/api";
import { DataType } from "./types";

export const GENERIC_FILTER = ({
  filterType = FilterType.STRING,
  ...otherProps
}: FilterSchemaItem): FilterSchemaItem => ({
  filterType,
  ...otherProps,
});

export const VEHICLES_GENERIC_FILTER = ({
  filterType = FilterType.STRING,
  ...otherProps
}: FilterSchemaItem): FilterSchemaItem => ({
  filterType,
  loadOptionsFunc: vehicleLoadDataFunc,
  ...otherProps,
});

export const VEHICLE_TAGS_GENERIC_FILTER = ({
  filterType = FilterType.STRING,
  ...otherProps
}: FilterSchemaItem): FilterSchemaItem => ({
  filterType,
  loadOptionsFunc: vehicleTagsLoadDataFunc,
  ...otherProps,
  disableSelectFilters: false,
  filterDataType: DataType.STRING,
  loadDataOnOpen: true,
});

export const GROUPS_GENERIC_FILTER = ({
  filterType = FilterType.STRING,
  ...otherProps
}: FilterSchemaItem): FilterSchemaItem => ({
  filterType,
  loadOptionsFunc: groupsLoadDataFunc,
  ...otherProps,
});

export const WORK_ORDERS_GENERIC_FILTER = ({
  filterType = FilterType.STRING,
  ...otherProps
}: FilterSchemaItem): FilterSchemaItem => ({
  filterType,
  loadOptionsFunc: workOrderLoadDataFunc,
  ...otherProps,
});

export const WORK_ORDER_PARTS_GENERIC_FILTER = ({
  filterType = FilterType.STRING,
  ...otherProps
}: FilterSchemaItem): FilterSchemaItem => ({
  filterType,
  loadOptionsFunc: workOrderPartLoadDataFunc,
  ...otherProps,
});

export const SMART_GROUPS_FILTER = ({
  ...otherProps
}: FilterSchemaItem): FilterSchemaItem => ({
  ...otherProps,
  fieldName: "smartGroups",
  loadOptionsFunc: smartGroupsLoadDataFunc,
  transformInitialSelectedFunc: transformInitialSelectedSmartGroup,
  getValueNameFunc: (id: string) =>
    getSmartGroup({ id }).then(({ data }) => data.name),
  search: true,
  loadDataOnOpen: true,
  whitelistedFilterOperators: [FilterOperator.IN, FilterOperator.NOT_IN],
});

export const SMART_GROUPS_V2_FILTER = ({
  eventType,
  ...otherProps
}: FilterSchemaItem & { eventType: string }): FilterSchemaItem => ({
  ...otherProps,
  fieldName: "smartGroups",
  loadOptionsFunc: smartGroupsV2LoadDataFunc(eventType),
  transformInitialSelectedFunc: transformInitialSelectedSmartGroupV2,
  getValueNameFunc: (id: string) =>
    getSmartGroupV2({ id }).then(({ data }) => data.name),
  search: true,
  loadDataOnOpen: true,
  whitelistedFilterOperators: [FilterOperator.IN, FilterOperator.NOT_IN],
});

export const SERVICE_RECORDS_GENERIC_FILTER = ({
  ...otherProps
}: FilterSchemaItem): FilterSchemaItem => ({
  loadOptionsFunc: serviceRecordsLoadDataFunc,
  ...otherProps,
});

export const CLAIMS_GENERIC_FILTER = (
  props: FilterSchemaItem
): FilterSchemaItem => ({
  ...props,
  loadOptionsFunc: claimsLoadDataFunc,
});

export const VECTOR_CLUSTERS_GENERIC_FILTER = (
  props: FilterSchemaItem
): FilterSchemaItem => ({
  ...props,
  loadOptionsFunc: vectorClustersLoadDataFunc,
  transformInitialSelectedFunc: transformInitialSelectedVectorCluster,
});

export const INSPECTIONS_GENERIC_FILTER = (
  props: FilterSchemaItem
): FilterSchemaItem => ({
  ...props,
  loadOptionsFunc: inspectionsLoadDataFunc,
});

export const REPAIRS_GENERIC_FILTER = (
  props: FilterSchemaItem
): FilterSchemaItem => ({
  ...props,
  loadOptionsFunc: repairsLoadDataFunc,
});

export const CUSTOM_RECORDS_GENERIC_FILTER = (
  props: FilterSchemaItem
): FilterSchemaItem => ({
  ...props,
  loadOptionsFunc: customRecordsLoadDataFunc,
});

export const FAILURE_MODES_GENERIC_FILTER = (
  props: FilterSchemaItem
): FilterSchemaItem => ({
  ...props,
  loadOptionsFunc: failureModesLoadDataFunc,
});

export const VEHICLES_ECUS_GENERIC_FILTER = ({
  filterType = FilterType.STRING,
  ...otherProps
}: FilterSchemaItem): FilterSchemaItem => ({
  filterType,
  loadOptionsFunc: vehicleECUsLoadDataFunc,
  ...otherProps,
  disableFiltering: true,
});

export const VEHICLES_OPTIONS_GENERIC_FILTER = ({
  filterType = FilterType.STRING,
  ...otherProps
}: FilterSchemaItem): FilterSchemaItem => ({
  filterType,
  loadOptionsFunc: vehicleOptionsLoadDataFunc,
  ...otherProps,
  disableFiltering: true,
});

export const VEHICLES_PARTS_GENERIC_FILTER = ({
  filterType = FilterType.STRING,
  ...otherProps
}: FilterSchemaItem): FilterSchemaItem => ({
  filterType,
  loadOptionsFunc: vehiclePartsLoadDataFunc,
  ...otherProps,
  disableFiltering: true,
});

export const MANUFACTURE_LINE_EXITS_GENERIC_FILTER = ({
  filterType = FilterType.STRING,
  ...otherProps
}: FilterSchemaItem): FilterSchemaItem => ({
  filterType,
  loadOptionsFunc: manufactureLineExitsLoadDataFunc,
  ...otherProps,
  disableFiltering: true,
});

export const TENANTS_GENERIC_FILTER = ({
  filterType = FilterType.STRING,
  ...otherProps
}: FilterSchemaItem): FilterSchemaItem => ({
  filterType,
  loadOptionsFunc: loadTenantsFunc,
  ...otherProps,
});

export const SIGNAL_EVENT_OCCURRENCES_GENERIC_FILTER = (
  props: FilterSchemaItem
): FilterSchemaItem => ({
  ...props,
  loadOptionsFunc: signalEventsOccurrencesLoadDataFunc,
  disableFiltering: true,
});

// V2 variant: no hardcoded V1 loader, so options load from the filter's
// `valuesEndpoint` (the V2 `/api/v2/signalEvents/occurrences/values/:field` route).
export const SIGNAL_EVENT_OCCURRENCES_V2_GENERIC_FILTER = (
  props: FilterSchemaItem
): FilterSchemaItem => ({
  ...props,
  disableFiltering: true,
});

export const SENSOR_READINGS_GENERIC_FILTER = (
  props: FilterSchemaItem
): FilterSchemaItem => ({
  ...props,
  loadOptionsFunc: loadSensorReadingsValuesFunc,
  disableFiltering: true,
});

export const SENSOR_ID_FILTER = (
  props: FilterSchemaItem
): FilterSchemaItem => ({
  ...props,
  loadOptionsFunc: loadSensorValuesFunc,
  transformInitialSelectedFunc: transformInitialSelectedSensors,
  disableFiltering: true,
});

export const ISSUES_GENERIC_FILTER = ({
  filterType = FilterType.STRING,
  ...otherProps
}: FilterSchemaItem): FilterSchemaItem => ({
  filterType,
  loadOptionsFunc: issuesLoadDataFunc,
  ...otherProps,
});

export const INVESTIGATIONS_GENERIC_FILTER = ({
  filterType = FilterType.STRING,
  ...otherProps
}: FilterSchemaItem): FilterSchemaItem => ({
  filterType,
  loadOptionsFunc: investigationsLoadDataFunc,
  ...otherProps,
});

export const ISSUES_DASHBOARD_FILTER = ({
  filterType = FilterType.STRING,
  ...otherProps
}: FilterSchemaItem): FilterSchemaItem => ({
  filterType,
  ...otherProps,
});

export const DEALERS_GENERIC_FILTER = ({
  filterType = FilterType.STRING,
  ...otherProps
}: FilterSchemaItem): FilterSchemaItem => ({
  filterType,
  loadOptionsFunc: dealersLoadDataFunc,
  ...otherProps,
});

export const CUSTOM_SIGNAL_EVENT_DEFINITIONS_GENERIC_FILTER = ({
  search = false,
  ...otherProps
}: FilterSchemaItem): FilterSchemaItem => ({
  search,
  loadOptionsFunc: customSignalEventDefinitionsLoadDataFunc,
  ...otherProps,
});

export const SERVICE_CENTERS_GENERIC_FILTER = ({
  filterType = FilterType.STRING,
  ...otherProps
}: FilterSchemaItem): FilterSchemaItem => ({
  filterType,
  loadOptionsFunc: serviceCentersLoadDataFunc,
  ...otherProps,
});

export const TRANSPORT_CATEGORY_FILTER = (t: TFunction): FilterSchemaItem => ({
  label: t("pages:vin_view.service_schedule.transport_category"),
  fieldName: "transportCategoryID",
  loadOptionsFunc: transportCategoriesLoadDataFunc,
  transformInitialSelectedFunc: transformInitialSelectedTransportCategory,
  disableFiltering: true,
});

export const EVENT_TYPE_FILTER = (
  t: TFunction,
  filter?: string
): FilterSchemaItem => ({
  label: t("common:type"),
  fieldName: "type",
  loadDataOnOpen: true,
  search: true,
  loadOptionsFunc: (args: APIListValuesRequest) =>
    serviceRecordsLoadDataFunc({ ...args, filter }),
});

export const DATE_FILTER_GENERIC = ({
  ...otherProps
}: FilterSchemaItem): FilterSchemaItem => ({
  ...otherProps,
  filterType: FilterType.DATE,
  disableSelectFilters: true,
});

export const SIGNAL_EVENTS_GENERIC_FILTER = ({
  ...props
}: FilterSchemaItem): FilterSchemaItem => ({
  loadOptionsFunc: signalEventsValuesLoadDataFunc,
  ...props,
});

export const GENERIC_FILTER_WITHOUT_SELECT = ({
  ...props
}: FilterSchemaItem): FilterSchemaItem => ({
  ...props,
  disableSelectFilters: true,
  disableFiltering: true,
});

export const PARTS_GENERIC_FILTER = ({
  filterType = FilterType.STRING,
  ...otherProps
}: FilterSchemaItem): FilterSchemaItem => ({
  filterType,
  loadOptionsFunc: partsLoadDataFunc,
  ...otherProps,
});

export const CREATED_AT_FILTER = (t: TFunction): FilterSchemaItem => ({
  label: t("common:columns.created"),
  fieldName: "createdAt",
  filterType: FilterType.DATE,
  filterDataType: DataType.DATE_WITH_TIME_UTC,
  disableSelectFilters: true,
});

export const UPDATED_AT_FILTER = (t: TFunction): FilterSchemaItem => ({
  label: t("common:update"),
  fieldName: "updatedAt",
  filterType: FilterType.DATE,
  filterDataType: DataType.DATE_WITH_TIME_UTC,
  disableSelectFilters: true,
});

export const FAILURE_MODE_EVENTS_GENERIC_FILTER = (
  id: string,
  props: FilterSchemaItem
): FilterSchemaItem => ({
  ...props,
  loadOptionsFunc: (args: APIListValuesRequest) =>
    failureModeEventLoadDataFunc({ id, ...args }),
});

export const FAILURE_MODE_RISK_MODEL_PREDICTIONS_GENERIC_FILTER = (
  id: string,
  props: FilterSchemaItem
): FilterSchemaItem => ({
  ...props,
  loadOptionsFunc: (args: APIListValuesRequest) =>
    failureModeRiskModelPredictionLoadDataFunc({ id, ...args }),
});

export const VEHICLE_RISK_MODEL_PREDICTIONS_GENERIC_FILTER = (
  vin: string,
  props: FilterSchemaItem
): FilterSchemaItem => ({
  ...props,
  loadOptionsFunc: (args: APIListValuesRequest) =>
    vehicleRiskModelPredictionLoadDataFunc({ vin, ...args }),
});

export const RISK_MODEL_PREDICTIONS_GENERIC_FILTER = (
  props: FilterSchemaItem
): FilterSchemaItem => ({
  ...props,
  loadOptionsFunc: (args: APIListValuesRequest) =>
    riskModelPredictionLoadDataFunc(args),
});

export const FAILURE_MODE_FILTER = (t: TFunction): FilterSchemaItem => ({
  label: t("common:event_type.failuremode_one"),
  fieldName: "failureModeID",
  loadOptionsFunc: failureModeLoadDataFunc,
  transformInitialSelectedFunc: transformInitialSelectedFailureMode,
  disableFiltering: true,
});
